/**
 * Compact rendering for the counts the panels put on screen: `1.2K` rather than `1,247`.
 *
 * The funnel shows a count in three fixed-width columns and one badge, and the widest real value
 * decides the column width for every row. A funnel over a large cohort reaches nine figures, so sizing
 * the columns for `247,891,043` left the common four-digit case swimming in whitespace, and sizing them
 * for the common case wrapped the large one — which offsets that row's bar from its neighbours' and
 * breaks the by-eye comparison the bars exist for. A bounded form fixes the width at five characters
 * and stops the columns from being a function of the largest number in the payload.
 *
 * Compacting loses precision, so it is never the only form on screen: everything rendered through
 * `CountValue` carries the exact number in its tooltip. Read this file and that component together —
 * a compact count with no exact counterpart within reach is a count the reader cannot act on.
 */

import { UnitText } from '../interfaces/panel-data';
import { pluraliseUnit } from './format-unit';

/** Ascending, so the scan below can stop at the first threshold the count does not reach. */
const SCALES: ReadonlyArray<{ suffix: string; divisor: number }> = [
  { suffix: 'K', divisor: 1e3 },
  { suffix: 'M', divisor: 1e6 },
  { suffix: 'B', divisor: 1e9 },
];

/** Below this many units, one decimal is shown; at or above it, the fraction is noise. */
const DECIMAL_CEILING = 10;

/**
 * Rejects the values that would otherwise reach the screen as the literal text "NaN" or "undefined".
 *
 * Thrown rather than defaulted to zero, matching the funnel panel's `lossSummary`: a missing count is a
 * malformed payload, and "0 pages lost" is a specific, false, and entirely plausible-looking claim. The
 * panel's ErrorBoundary turns the throw into a contained "failed to load" state, which tells the reader
 * the truth — that the number is not available.
 */
const requireFinite = (count: number): number => {
  if (!Number.isFinite(count)) {
    throw new Error(`Panel payload carried a non-numeric count: ${count}`);
  }
  return count;
};

/**
 * `1.2K`, `18M`, `847` — the count in at most five characters.
 *
 * Truncated rather than rounded, so the compact form never overstates. `1,999` reads as `1.9K`, not the
 * `2K` that rounding gives: a reader comparing it against a `2.0K` sibling would otherwise see two
 * equal figures where the underlying counts differ by a hundred. Truncation also removes the carry
 * problem outright — rounding can turn 999,950 into `1000K`, a five-digit mantissa in a form whose
 * whole purpose is bounded width.
 *
 * One decimal below ten units, none at or above: `9.4K` then `18K`. At two significant figures the
 * fraction has stopped distinguishing anything a reader would act on, and dropping it keeps the width
 * flat as the counts grow.
 */
export const formatCompactCount = (count: number): string => {
  const value = requireFinite(count);
  const magnitude = Math.abs(value);

  // Under a thousand there is nothing to compact: the exact number already fits, and `0.8K` would be
  // both longer and less precise than `847`. Truncated all the same, because `toLocaleString` keeps up to
  // three fraction digits — so a fractional count would leave this branch as `847.679`, seven characters
  // wide in a form whose contract is five, and would be the one count on the panel with a fraction the
  // suffixed branch would have dropped. Counts are integers on the wire, so this only fires on a payload
  // that is already wrong; truncating toward zero is the same never-overstate rule applied below.
  if (magnitude < SCALES[0].divisor) {
    return Math.trunc(value).toLocaleString();
  }

  const scale = SCALES.filter((s) => magnitude >= s.divisor).pop() as (typeof SCALES)[number];
  const scaled = value / scale.divisor;
  const truncated =
    Math.abs(scaled) < DECIMAL_CEILING ? Math.trunc(scaled * 10) / 10 : Math.trunc(scaled);

  // `2.0K` is spelled `2K`; toLocaleString drops the trailing zero, and groups the thousands for the
  // one case that still needs it — a count in the hundreds of billions.
  return `${truncated.toLocaleString()}${scale.suffix}`;
};

/** The count exactly, grouped for reading: `1,247`. This is what the tooltips carry. */
export const formatExactCount = (count: number): string => requireFinite(count).toLocaleString();

/**
 * The compact count with its unit, e.g. `1.2K pages` — or the bare `1.2K` when no unit is configured.
 *
 * Pluralised against the *exact* count, not the compact one. `1.0K` truncates to `1K`, and pluralising
 * off that mantissa would render "1K page" for a thousand pages.
 */
export const formatCompactUnitCount = (count: number, text: UnitText): string => {
  const unit = pluraliseUnit(text, count);
  const formatted = formatCompactCount(count);
  return unit ? `${formatted} ${unit}` : formatted;
};
